import React, { useState, useMemo } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Box, Popover, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'

import { toast } from 'components/global/CUK/Toast/Toast'
import Checkbox from 'packages/ui/Checkbox'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { createExpandedRowComponent, useResponsiveTableContext } from 'packages/ui/Table'

import {
  ActivateUser,
  DeactivateUser,
  NoSyncUser,
  PendingUser,
  LockedUser,
  PasswordLocked,
} from 'pages/Contacts/StatusContacts'
import { OPERATION_FIND_CONTACT_SITES } from 'pages/Contacts/operations-gql/queries'
import useFilters, { useContactsSelected, useLastContactCheckedState } from '../../store'

const LIMIT_CONTACTS = 100

const HeaderCheckbox = (ctx) => {
  const [contactsSelected, setContactsSelected] = useContactsSelected()
  const [filters] = useFilters()
  
  const isForSync = filters.sync.length === 1 && filters.sync.includes('not_sync')
  
  const selectableContacts = isForSync 
    ? ctx.items.filter(contact => !contact.sync).slice(0, LIMIT_CONTACTS)
    : ctx.items.filter(contact => contact.sync).slice(0, LIMIT_CONTACTS)
  
  const hasSelection = contactsSelected.length > 0
  
  return (
    <Checkbox
      checked={false}
      indeterminate={hasSelection}
      onChange={() => {
        if (hasSelection) {
          setContactsSelected([])
        } else {
          setContactsSelected(selectableContacts)
        }
      }}
    />
  )
}

const BodyCheckbox = ({ contact, ctx }) => {
  const {t} = useTranslation()
  const [contactsSelected, setContactsSelected] = useContactsSelected()
  const [lastContactChecked, setLastContactChecked] = useLastContactCheckedState()
  const [filters] = useFilters()
  
  const isForSync = filters.sync.length === 1 && filters.sync.includes('not_sync')
  const isSelected = contactsSelected.some(selected => selected.sf_id === contact.sf_id)
  const isDisabled = isForSync ? contact.sync : !contact.sync 
  
  const handleMultiSelect = () => {
    const fromIndex = ctx.items.findIndex(item => item.sf_id === lastContactChecked.sf_id)
    const toIndex = ctx.items.findIndex(item => item.sf_id === contact.sf_id)
    const start = Math.min(toIndex, fromIndex)
    const end = Math.max(toIndex, fromIndex)
    const newItems = []
    for (let i = start; i <= end; i++) {
      newItems.push(ctx.items[i])
    }
    setLastContactChecked(null)
    setContactsSelected(prev => {
      const prevSfIds = prev.map(({sf_id}) => sf_id)
      return [...prev, ...newItems.filter(item => !prevSfIds.includes(item.sf_id))]
    })
  }

  const handleChange = (event) => {
    event.stopPropagation()
    if (isDisabled) return
    if (event.nativeEvent.shiftKey && lastContactChecked !== null) {
      handleMultiSelect()
    } else {
      if (isSelected) {
        setContactsSelected(prev => prev.filter(selected => selected.sf_id !== contact.sf_id))
      } else {
        if (contactsSelected.length === LIMIT_CONTACTS) {
          toast.error(t('auth.card_in_mass.sync.limit_exceed'))
        } else {
          setLastContactChecked(contact)
          setContactsSelected(prev => [...prev, contact]);
        }
      }
    }
  }
  
  return (
    <Checkbox
      checked={isSelected}
      disabled={isDisabled}
      onChange={handleChange}
      onClick={(event) => event.stopPropagation()}
    />
  )
}

const Status = ({ contact }) => {
  if (contact.state === 'locked') {
    return <LockedUser table />
  } else if (!contact.sync) {
    return <NoSyncUser table />
  } else if (contact.state === 'active') {
    return <ActivateUser table />
  } else if (contact.state === 'unconfirmed') {
    return <PendingUser table />
  } else if (contact.state === 'deactivated') {
    return <DeactivateUser table />
  } else if (contact.state === 'password_locked') {
    return <PasswordLocked table />
  }
  return null
}

const TooltipSites = ({ contact }) => {
  const { t } = useTranslation()
  const { internal } = useFilters()
  const [findContactSites, { loading, data }] = useLazyQuery(
    OPERATION_FIND_CONTACT_SITES,
  )
  const [anchorEl, setAnchorEl] = useState(null)

  const handlePopoverOpen = async (event) => {
    if (contact.nb_sites === 0 || internal) {
      return
    }
    setAnchorEl(event.target)
    await findContactSites({ variables: { sf_id: contact.sf_id } })
  }
  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  return (
    <Box>
      <Box onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}>
        {internal ? '-' : contact.nb_sites}
      </Box>
      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack color={'white'} bgcolor={'grey.700'} p={'4px 6px'}>
          {loading ? (
            <Typography fontSize={11} fontWeight={500}>{t('ui_elements.loading_msg')}</Typography>
          ) : data?.sites.length > 10 ? (
            <Typography fontSize={11} fontWeight={500}>
              {' '}
              {t('auth.tables.tooltip_sites')}
            </Typography>
          ) : (
            data?.sites.map((site) => (
              <Typography fontSize={11} fontWeight={500}>
                <li>{site.name}</li>
              </Typography>
            ))
          )}
        </Stack>
      </Popover>
    </Box>
  )
}

const Crown = ({ code }) => {
  const { t } = useTranslation()
  const color = useMemo(() => {
    if (code === 'admin') {
      return 'primary.main'
    } else if (code === 'roles_and_contact') {
      return '#92BC62'
    }
  }, [code])
  const tooltip = useMemo(() => {
    if (code === 'admin') {
      return 'auth.account_status.internal'
    } else if (code === 'roles_and_contact') {
      return 'auth.admin.role.admin_roles_and_contact_description'
    }
  }, [code])
  return (
    <Tooltip
      title={t(tooltip)}
      placement={'top'}
      arrow
      sx={{ maxWidth: '40px' }}
    >
      <Box sx={{ 
        color,
        '& i': {
          fontWeight: 900,
        }
      }}>
        <i className="fa-solid fa-crown" style={{ fontWeight: 900 }} />
      </Box>
    </Tooltip>
  )
}

const Name = ({ contact }) => {
  return (
    <Stack justifyContent={'space-between'} direction={'row'} gap={1}>
      <Typography variant='caption'>
        {contact.last_name} {contact.first_name}
      </Typography>
      <Stack direction={'row'} gap={.5}>
        {
          contact.admin
            ? <Crown code={'admin'} />
            : null
        }
        {
          !contact.admin && contact?.roles?.admin_roles && contact?.roles?.admin_contact
            ? <Crown code={'roles_and_contact'} />
            : null
        }
      </Stack>
    </Stack>
  )
}

const EmailCell = ({ contact }) => {
  return (
    <Tooltip title={contact.email} placement="top" arrow>
      <Typography 
        variant='caption'
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block'
        }}
      >
        {contact.email}
      </Typography>
    </Tooltip>
  )
}

const ActionsWithExpand = ({ contact, ctx }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const responsiveTableContext = useResponsiveTableContext()

  if (!isMobile || !responsiveTableContext) {
    return null
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'flex-end',
      width: '100%'
    }}>
      <CardActionButton
        tooltiptitle={responsiveTableContext.isRowExpanded(contact.sf_id) ? ctx.t('contacts.actions.collapse_details') : ctx.t('contacts.actions.expand_details')}
        iconlabel={responsiveTableContext.isRowExpanded(contact.sf_id) ? 'chevron-up' : 'chevron-down'}
        onClick={(e) => {
          e.stopPropagation()
          responsiveTableContext.toggleRowExpansion(contact.sf_id)
        }}
      />
    </Box>
  )
}

export const ExpandedRowDetails = createExpandedRowComponent((contact, t) => [
  {
    key: 'phone',
    label: t('auth.tables.header.mobile_phone'),
    value: contact.mobile_phone || '-'
  },
  {
    key: 'sites',
    label: t('auth.tables.header.site'),
    value: contact.nb_sites?.toString() || '-'
  },
  {
    key: 'last_connexions',
    label: t('auth.tables.header.last_login'),
    value: contact.last_sign_in_at
      ? new Date(contact.last_sign_in_at).toLocaleDateString()
      : '-'
  },
  {
    key: 'creation_date',
    label: t('auth.tables.header.created_at'),
    value: contact.created_at
      ? new Date(contact.created_at).toLocaleDateString()
      : '-'
  }
])

const COLUMNS_CONFIG = [
  {
    name: 'check',
    header: {
      label: HeaderCheckbox,
    },
    style: {
      width: 24,
      paddingLeft: 8,
    },
    value: (contact, ctx) => <BodyCheckbox contact={contact} ctx={ctx}/>,
    priority: 1
  },
  {
    name: 'availability',
    header: {
      label: (ctx) => ctx.t('coupons.table.status_col'),
    },
    value: (contact) => <Status contact={contact} />,
    minWidth: 'xs',
    priority: 1
  },
  {
    name: 'name',
    header: {
      label: (ctx) => ctx.t('auth.tables.header.name'),
    },
    value: (contact) => <Name contact={contact} />,
    sortBy: 'last_name',
    sortable: true,
    minWidth: 'xs',
    priority: 1,
    style: {
      maxWidth: '200px',
    }
  },
  {
    name: 'email',
    header: {
      label: (ctx) => ctx.t('auth.tables.header.email'),
    },
    value: (contact) => <EmailCell contact={contact} />,
    sortable: true,
    minWidth: 'xs',
    priority: 1,
    style: {
      minWidth: '100px',
      maxWidth: '200px',
      overflow: 'hidden'
    }
  },
  {
    name: 'phone',
    header: {
      label: (ctx) => ctx.t('auth.tables.header.mobile_phone'),
    },
    value: (contact) => (contact.mobile_phone ? contact.mobile_phone : '-'),
    minWidth: 'lg',
    priority: 3
  },
  {
    name: 'sites',
    header: {
      label: (ctx) => ctx.t('auth.tables.header.site'),
    },
    value: (contact) => <TooltipSites contact={contact} />,
    minWidth: 'lg',
    priority: 2
  },
  {
    name: 'last_connexions',
    header: {
      label: (ctx) => ctx.t('auth.tables.header.last_login'),
    },
    value: (contact) =>
      contact.last_sign_in_at
        ? new Date(contact.last_sign_in_at).toLocaleDateString()
        : '-',
    minWidth: 'lg',
    priority: 3
  },
  {
    name: 'creation_date',
    header: {
      label: (ctx) => ctx.t('auth.tables.header.created_at'),
    },
    value: (contact) =>
      contact.created_at
        ? new Date(contact.created_at).toLocaleDateString()
        : '-',
    sortBy: 'created_at',
    sortable: true,
    minWidth: 'lg',
    priority: 3
  },
  {
    name: 'actions',
    header: {
      label: () => '',
    },
    value: (contact, ctx) => <ActionsWithExpand contact={contact} ctx={ctx} />,
    style: { width: '60px', textAlign: 'right' },
    priority: 1
  }
]

export default COLUMNS_CONFIG
